import React, { useContext, useState } from 'react';
import GooglePlacesAutocomplete, { geocodeByPlaceId } from 'react-google-places-autocomplete';
import { LocationContext } from '../context/LocationContext';
import { FaTimes } from 'react-icons/fa';
import { FaLocationCrosshairs } from "react-icons/fa6";
import { toast } from 'sonner';
import { PuffLoader } from "react-spinners";

const LocationModal = () => {
  const { isLocationModalOpen, setIsLocationModalOpen, fetchAddressFromLatLng, locationError, location } = useContext(LocationContext);
  const [loading, setLoading] = useState(false);

  // ✅ Handle user selecting a place
  const handleSelect = async (place) => {
    try {
     
      setLoading(true); // show loader

      const results = await geocodeByPlaceId(place.value.place_id);

      if (results && results[0]) {
        const parsedAddress = parseAddressComponents(results[0]);

        const newLocation = {
          ...parsedAddress,
          timestamp: new Date().toISOString(),
        };
 
        localStorage.setItem("userLocation", JSON.stringify(newLocation));

        fetchAddressFromLatLng(newLocation.lat, newLocation.lng);

        // reload site after 1s so loader is visible briefly
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (error) {
      setLoading(false);
      toast.error("Error selecting location: " + error.message);
    }
  };

  // ✅ Retry geolocation
  const retryGeolocation = () => {
    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude: lat, longitude: lng } = position.coords;
        fetchAddressFromLatLng(lat, lng);

        localStorage.setItem("userLocation", JSON.stringify({
          lat,
          lng,
          timestamp: new Date().toISOString(),
        }));

        setTimeout(() => {
          window.location.reload();
        }, 1000);
      },
      (error) => {
        setLoading(false);
        if (error.code === error.PERMISSION_DENIED) {
          toast.info("Location access was blocked. Please allow permissions.");
        } else {
          toast.error("Error retrying location: " + error.message);
        }
      }
    );
  };

  // ✅ Helper parser
  const parseAddressComponents = (result) => {
    const components = result.address_components;
    const geometry = result.geometry;

    let name = "";
    let area = "";
    let city = "";
    let state = "";
    let countryCode = "";
    let pincode = "";

    components.forEach((comp) => {
      if (comp.types.includes("premise") || comp.types.includes("street_number")) {
        name = comp.long_name;
      } else if (comp.types.includes("route")) {
        area = comp.long_name;
      } else if (
        comp.types.includes("sublocality") ||
        comp.types.includes("sublocality_level_1")
      ) {
        if (!area) {
          area = comp.long_name;
        } else {
          area = `${area}, ${comp.long_name}`;
        }
      } else if (comp.types.includes("locality")) {
        city = comp.long_name;
      } else if (comp.types.includes("administrative_area_level_1")) {
        state = comp.long_name;
      } else if (comp.types.includes("country")) {
        countryCode = comp.short_name;
      } else if (comp.types.includes("postal_code")) {
        pincode = comp.long_name;
      }
    });

    return {
      name,
      address: result.formatted_address,
      area,
      city,
      state,
      countryCode,
      lat: geometry.location.lat(),
      lng: geometry.location.lng(),
      pincode,
    };
  };

  return (
    <>
      {isLocationModalOpen && (
        <div className="fixed px-2 inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 text-center relative">
            {location && (
              <button
                onClick={() => setIsLocationModalOpen(false)}
                className="absolute top-4 right-4 text-theme2 text-xl hover:text-black transition-colors"
              >
                <FaTimes />
              </button>
            )}
            <button
              onClick={retryGeolocation}
              className="absolute top-4 left-4 w-8 h-8 bg-theme2 text-white rounded-full flex justify-center items-center hover:bg-black transition-colors"
            >
              <FaLocationCrosshairs />
            </button>
            <h2 className="text-xl font-bold mb-4 text-theme2">Select Your Location</h2>
            <p className="text-gray-600 mb-4">We need your location to show nearby restaurants.</p>
            <div className="mb-4">
              <GooglePlacesAutocomplete
                apiKey="AIzaSyCo5zCVQSeC9qaDTElGlakIt5tOfbuFXW4"
                debounce={500}
                selectProps={{
                  onChange: handleSelect,
                  placeholder: 'Enter your address...',
                  styles: {
                    input: (provided) => ({
                      ...provided,
                      padding: '10px 10px 10px 0',
                      width: '100%',
                      textAlign: 'left',
                      color: '#4B5563',
                      borderRadius: '0.375rem',
                      outline: 'none',
                    }),
                    option: (provided) => ({
                      ...provided,
                      color: '#333',
                      textAlign: 'left',
                    }),
                  },
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* ✅ Fullscreen Loader */}
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <PuffLoader color={"#83442B"} size={60} />
        </div>
      )}
    </>
  );
};

export default LocationModal;
